import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader2, FileText, FolderOpen, ListTodo } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, getDocs, limit, orderBy, query, Timestamp } from 'firebase/firestore';
import type { CRMUser, EnhancedDeal, OpsCategory, OpsTask, TaskPriority, TaskStatus, TimberProject } from '../../types';
import { pushExecAttachment } from '../../utils/execInboxApi';
import {
  resolvePushTargets,
  resolvePushTargetsFromCandidate,
  type PushDestinationKind,
} from '../../utils/execInboxLinkResolve';
import { searchOpsHub } from '../../utils/hubSearch/searchOpsHub';
import { db } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import { applyPlainCommentListKeydown } from '../../utils/richCommentListShortcuts';
import type { LinkCandidate } from './ExecThreadPanel';

interface AttachmentMeta {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

type OpsMode = 'existing' | 'new';

interface Props {
  open: boolean;
  mailboxUserId: string;
  threadId: string;
  threadSubject?: string;
  emailThreadId?: string;
  messageId: string;
  messageDate?: string;
  attachment: AttachmentMeta;
  linkedTo?: { type: 'lead' | 'deal' | 'project'; id: string; name: string } | null;
  linkCandidates: LinkCandidate[];
  deals: EnhancedDeal[];
  timberProjects: TimberProject[];
  crmUsers: CRMUser[];
  onClose: () => void;
  onToast: (msg: string, error?: boolean) => void;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ExecAttachmentPushModal: React.FC<Props> = ({
  open,
  mailboxUserId,
  threadId,
  threadSubject = '',
  emailThreadId,
  messageId,
  messageDate,
  attachment,
  linkedTo,
  linkCandidates,
  deals,
  timberProjects,
  crmUsers,
  onClose,
  onToast,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kind, setKind] = useState<PushDestinationKind>('lead_document');
  const [selectedLink, setSelectedLink] = useState<LinkCandidate | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pushing, setPushing] = useState(false);
  const [done, setDone] = useState<{ docId: string; url: string; kind: string; taskId?: string } | null>(null);

  const [opsMode, setOpsMode] = useState<OpsMode>('existing');
  const [opsTasks, setOpsTasks] = useState<OpsTask[]>([]);
  const [opsTasksLoading, setOpsTasksLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [opsNote, setOpsNote] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState<OpsCategory>('requests');
  const [newPriority, setNewPriority] = useState<TaskPriority>('medium');
  const [newAssignee, setNewAssignee] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newInitialComment, setNewInitialComment] = useState('');

  const activeLink = selectedLink || (linkedTo as LinkCandidate | null);

  const targets = useMemo(
    () => resolvePushTargets(activeLink, deals, timberProjects),
    [activeLink, deals, timberProjects],
  );

  const activeTeam = useMemo(
    () => crmUsers.filter((u) =>
      u.isActive && (u.role === 'employee' || u.role === 'admin' || u.role === 'staff'),
    ),
    [crmUsers],
  );

  const loadOpsTasks = useCallback(async () => {
    setOpsTasksLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'operationsTasks'), orderBy('createdAt', 'desc'), limit(100)),
      );
      const tasks = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          startDate: data.startDate?.toDate?.(),
          dueDate: data.dueDate?.toDate?.(),
        } as OpsTask;
      }).filter((t) => t.status !== 'completed');
      setOpsTasks(tasks);
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setOpsTasksLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    if (!open) return;
    setSelectedLink(null);
    setPickerOpen(false);
    setPickerQuery('');
    setPushing(false);
    setDone(null);
    setOpsMode('existing');
    setSelectedTaskId('');
    setTaskSearchQuery('');
    setOpsNote('');
    setNewTitle(threadSubject || '');
    setNewCategory('requests');
    setNewPriority('medium');
    setNewAssignee('');
    setNewDueDate('');
    setNewInitialComment('');
    const initial = resolvePushTargets(linkedTo as LinkCandidate | null, deals, timberProjects);
    const canCrm = initial.canLeadDocument || initial.canProjectFile;
    setKind(canCrm ? initial.defaultKind : 'ops_comment');
    void loadOpsTasks();
  }, [open, linkedTo, deals, timberProjects, threadSubject, loadOpsTasks]);

  useEffect(() => {
    if (kind === 'lead_document' && !targets.canLeadDocument && targets.canProjectFile) setKind('project_file');
    if (kind === 'project_file' && !targets.canProjectFile && targets.canLeadDocument) setKind('lead_document');
  }, [targets, kind]);

  const filteredCandidates = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return linkCandidates.slice(0, 10);
    return linkCandidates.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 10);
  }, [pickerQuery, linkCandidates]);

  const filteredOpsTasks = useMemo(() => {
    const results = searchOpsHub({
      query: taskSearchQuery,
      opsTasks,
      crmUsers,
      limit: 12,
    });
    return results
      .filter((r) => r.kind === 'opsTask')
      .map((r) => r.payload as OpsTask);
  }, [taskSearchQuery, opsTasks, crmUsers]);

  const selectedTask = useMemo(
    () => opsTasks.find((t) => t.id === selectedTaskId) || null,
    [opsTasks, selectedTaskId],
  );

  const entityLabel = kind === 'lead_document'
    ? (targets.leadName || 'Select a lead')
    : (targets.projectName || 'Select a project');

  const canSubmitCrm = kind === 'lead_document'
    ? !!targets.leadId && targets.canLeadDocument
    : !!targets.projectId && targets.canProjectFile;

  const canSubmitOps = opsMode === 'existing'
    ? !!selectedTaskId
    : !!newTitle.trim();

  const canSubmit = kind === 'ops_comment' ? canSubmitOps : canSubmitCrm;

  const createOpsTask = async (): Promise<string> => {
    if (!user?.id) throw new Error('Sign in required');
    const now = new Date();
    const taskData: Record<string, unknown> = {
      title: newTitle.trim(),
      description: '',
      category: newCategory,
      assignedTo: newAssignee ? [newAssignee] : [],
      collaborators: [],
      status: 'pending' as TaskStatus,
      priority: newPriority,
      startDate: Timestamp.fromDate(now),
      dueDate: newDueDate ? Timestamp.fromDate(new Date(`${newDueDate}T00:00:00`)) : null,
      estimatedHours: null,
      recurring: null,
      requestedBy: '',
      actionRequired: null,
      actionRequiredNote: null,
      subtasks: [],
      tags: [],
      origin: {
        kind: 'email',
        emailThreadId: emailThreadId || threadId,
        label: threadSubject || 'From email',
      },
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      completedAt: null,
      createdBy: user.id,
    };
    const docRef = await addDoc(collection(db, 'operationsTasks'), taskData);
    return docRef.id;
  };

  const handlePush = async () => {
    if (!canSubmit) {
      if (kind === 'ops_comment') {
        onToast(opsMode === 'existing' ? 'Choose an OPS task first' : 'Enter a task title', true);
      } else {
        onToast('Choose a linked lead or project first', true);
      }
      return;
    }
    setPushing(true);
    try {
      let taskId = selectedTaskId;
      let note = opsNote.trim();

      if (kind === 'ops_comment') {
        if (opsMode === 'new') {
          taskId = await createOpsTask();
          note = newInitialComment.trim();
        }
        const result = await pushExecAttachment(mailboxUserId, {
          threadId,
          messageId,
          attachmentId: attachment.id,
          filename: attachment.filename,
          mimeType: attachment.mimeType,
          destination: {
            kind: 'ops_comment',
            taskId,
            ...(note ? { note } : {}),
          },
        });
        setDone({ docId: result.docId, url: result.url, kind: result.kind, taskId });
        onToast('Saved to OPS task');
        return;
      }

      const destination = kind === 'lead_document'
        ? { kind: 'lead_document' as const, leadId: targets.leadId!, dealId: targets.dealId }
        : { kind: 'project_file' as const, projectId: targets.projectId! };
      const result = await pushExecAttachment(mailboxUserId, {
        threadId,
        messageId,
        attachmentId: attachment.id,
        filename: attachment.filename,
        mimeType: attachment.mimeType,
        destination,
      });
      setDone({ docId: result.docId, url: result.url, kind: result.kind });
      onToast(`Saved to ${kind === 'lead_document' ? 'Lead Documents' : 'Project Files'}`);
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setPushing(false);
    }
  };

  const openDestination = () => {
    if (done?.kind === 'ops_comment' && done.taskId) {
      navigate(`/operations?task=${encodeURIComponent(done.taskId)}`);
      onClose();
      return;
    }
    if (kind === 'lead_document' && targets.leadId) {
      navigate(`/client/${targets.leadId}`);
      onClose();
      return;
    }
    if (kind === 'project_file' && targets.projectId) {
      navigate(`/project-hub/${targets.projectId}`);
      onClose();
    }
  };

  const doneLabel = done?.kind === 'ops_comment'
    ? 'File saved to the OPS task.'
    : 'File saved to the CRM.';

  const openButtonLabel = done?.kind === 'ops_comment'
    ? 'Open in Operations'
    : 'Open in CRM';

  const submitLabel = kind === 'ops_comment'
    ? (pushing ? 'Saving…' : 'Save to OPS')
    : (pushing ? 'Saving…' : 'Push to CRM');

  if (!open) return null;

  return (
    <>
      <div className="exec-overlay" onClick={onClose} />
      <div className="exec-push-modal" role="dialog" aria-label="Save attachment">
        <div className="exec-push-head">
          <h3>Save attachment</h3>
          <button type="button" className="exec-so-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="exec-push-file">
          <FileText size={16} />
          <div>
            <div className="exec-push-filename">{attachment.filename}</div>
            <div className="exec-push-meta">
              {fmtSize(attachment.size)}
              {messageDate ? ` · ${new Date(messageDate).toLocaleString()}` : ''}
            </div>
          </div>
        </div>

        {done ? (
          <div className="exec-push-done">
            <p>{doneLabel}</p>
            <div className="exec-push-done-actions">
              <button type="button" className="exec-cta" onClick={openDestination}>{openButtonLabel}</button>
              <a className="exec-ghost-btn" href={done.url} target="_blank" rel="noopener noreferrer">View file</a>
            </div>
          </div>
        ) : (
          <>
            <div className="exec-push-tabs">
              <button
                type="button"
                className={`exec-push-tab${kind === 'lead_document' ? ' active' : ''}`}
                disabled={!targets.canLeadDocument && !activeLink}
                onClick={() => setKind('lead_document')}
              >
                <FileText size={13} /> Lead Documents
              </button>
              <button
                type="button"
                className={`exec-push-tab${kind === 'project_file' ? ' active' : ''}`}
                disabled={!targets.canProjectFile && !activeLink}
                onClick={() => setKind('project_file')}
              >
                <FolderOpen size={13} /> Project Files
              </button>
              <button
                type="button"
                className={`exec-push-tab${kind === 'ops_comment' ? ' active' : ''}`}
                onClick={() => setKind('ops_comment')}
              >
                <ListTodo size={13} /> OPS Task
              </button>
            </div>

            {kind === 'ops_comment' ? (
              <div className="exec-push-ops">
                <div className="exec-push-ops-modes">
                  <button
                    type="button"
                    className={`exec-push-ops-mode${opsMode === 'existing' ? ' active' : ''}`}
                    onClick={() => setOpsMode('existing')}
                  >
                    Existing task
                  </button>
                  <button
                    type="button"
                    className={`exec-push-ops-mode${opsMode === 'new' ? ' active' : ''}`}
                    onClick={() => setOpsMode('new')}
                  >
                    New task
                  </button>
                </div>

                {opsMode === 'existing' ? (
                  <>
                    <div className="exec-push-target">
                      <span className="exec-push-target-label">OPS task</span>
                      {selectedTask ? (
                        <div className="exec-push-target-row">
                          <span className="exec-link-chip">
                            <span className="exec-link-type">ops</span>
                            {selectedTask.title}
                          </span>
                          <button
                            type="button"
                            className="exec-ghost-btn"
                            onClick={() => setSelectedTaskId('')}
                          >
                            Change…
                          </button>
                        </div>
                      ) : (
                        <div className="exec-push-picker">
                          <input
                            className="exec-link-input"
                            placeholder="Search open OPS tasks…"
                            value={taskSearchQuery}
                            onChange={(e) => setTaskSearchQuery(e.target.value)}
                            autoFocus
                          />
                          <div className="exec-link-results">
                            {opsTasksLoading ? (
                              <div className="exec-push-ops-loading"><Loader2 size={14} className="spin" /> Loading tasks…</div>
                            ) : filteredOpsTasks.length === 0 ? (
                              <div className="exec-push-ops-empty">No open OPS tasks found</div>
                            ) : (
                              filteredOpsTasks.map((t) => (
                                <button
                                  key={t.id}
                                  type="button"
                                  className="exec-link-result"
                                  onClick={() => {
                                    setSelectedTaskId(t.id!);
                                    setTaskSearchQuery('');
                                  }}
                                >
                                  <span className="exec-link-type">{t.category}</span>
                                  <span>{t.title}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <label className="exec-push-ops-field">
                      <span className="exec-push-target-label">Note (optional)</span>
                      <textarea
                        className="exec-push-ops-textarea"
                        rows={2}
                        placeholder="Comment to add with this file…"
                        value={opsNote}
                        onChange={(e) => setOpsNote(e.target.value)}
                        onKeyDown={(e) => applyPlainCommentListKeydown(e, e.currentTarget, setOpsNote, 'enter')}
                      />
                    </label>
                  </>
                ) : (
                  <div className="exec-push-ops-form">
                    <label className="exec-push-ops-field">
                      <span className="exec-push-target-label">Title</span>
                      <input
                        className="exec-link-input"
                        placeholder="Task title…"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        autoFocus
                      />
                    </label>
                    <div className="exec-push-ops-grid">
                      <label className="exec-push-ops-field">
                        <span className="exec-push-target-label">Category</span>
                        <select
                          className="exec-push-ops-select"
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value as OpsCategory)}
                        >
                          <option value="admin">Admin</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="ordering">Ordering</option>
                          <option value="requests">Requests</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                      <label className="exec-push-ops-field">
                        <span className="exec-push-target-label">Priority</span>
                        <select
                          className="exec-push-ops-select"
                          value={newPriority}
                          onChange={(e) => setNewPriority(e.target.value as TaskPriority)}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </label>
                    </div>
                    <label className="exec-push-ops-field">
                      <span className="exec-push-target-label">Assignee</span>
                      <select
                        className="exec-push-ops-select"
                        value={newAssignee}
                        onChange={(e) => setNewAssignee(e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {activeTeam.map((u) => (
                          <option key={u.uid} value={u.uid}>{u.displayName}</option>
                        ))}
                      </select>
                    </label>
                    <label className="exec-push-ops-field">
                      <span className="exec-push-target-label">Due date (optional)</span>
                      <input
                        type="date"
                        className="exec-link-input"
                        value={newDueDate}
                        onChange={(e) => setNewDueDate(e.target.value)}
                      />
                    </label>
                    <label className="exec-push-ops-field">
                      <span className="exec-push-target-label">Comment (optional)</span>
                      <textarea
                        className="exec-push-ops-textarea"
                        rows={2}
                        placeholder="Context to add with the attachment…"
                        value={newInitialComment}
                        onChange={(e) => setNewInitialComment(e.target.value)}
                        onKeyDown={(e) => applyPlainCommentListKeydown(e, e.currentTarget, setNewInitialComment, 'enter')}
                      />
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <div className="exec-push-target">
                <span className="exec-push-target-label">Destination</span>
                <div className="exec-push-target-row">
                  <span className="exec-link-chip">
                    <span className="exec-link-type">{kind === 'lead_document' ? 'lead' : 'project'}</span>
                    {entityLabel}
                  </span>
                  <button
                    type="button"
                    className="exec-ghost-btn"
                    onClick={() => setPickerOpen((v) => !v)}
                  >
                    Change…
                  </button>
                </div>
                {!activeLink && (
                  <p className="exec-push-hint">Link this thread or pick a lead, deal, or project below.</p>
                )}
                {pickerOpen && (
                  <div className="exec-push-picker">
                    <input
                      className="exec-link-input"
                      placeholder="Search leads, deals, projects…"
                      value={pickerQuery}
                      onChange={(e) => setPickerQuery(e.target.value)}
                      autoFocus
                    />
                    <div className="exec-link-results">
                      {filteredCandidates.map((c) => (
                        <button
                          key={`${c.type}-${c.id}`}
                          type="button"
                          className="exec-link-result"
                          onClick={() => {
                            setSelectedLink(c);
                            const t = resolvePushTargetsFromCandidate(c, deals, timberProjects);
                            setKind(t.defaultKind);
                            setPickerOpen(false);
                            setPickerQuery('');
                          }}
                        >
                          <span className="exec-link-type">{c.type}</span>
                          <span>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="exec-push-foot">
              <button type="button" className="exec-ghost-btn" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="exec-cta"
                disabled={pushing || !canSubmit}
                onClick={handlePush}
              >
                {pushing ? <Loader2 size={14} className="spin" /> : null}
                {submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ExecAttachmentPushModal;
